import { useNavigate } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useObrasTopHeaderState } from "../../../obras/presentation/components/hooks/obras_top_header_state";

import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ShieldIcon from "@mui/icons-material/Shield";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import CommonConstants from "../../../../common/constants";

import { PrincipalDocumentosLista } from "../../../auditoria/domain/models/documentos_lista";
import { useFuncionariosTercerizacaoPagination } from "../hooks/Funcionarios_tercerizacao_lista/pagination";
import { TercIndicadores } from "../../domain/models/terc_indicadores";

type LoadPrincipalListaFuncState = {
  IdArquivoModulo?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number;
  pesquisa?: string;
};

export const useTercPrincipalViewModel = () => {
  const {
    commons: { getAllListagemDocUseCase, getGedArquivoDataUseCase },
    reports: {},
    terceirizacao: { getAllIndicadoresUseCase },
  } = useDependencyInjection();

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);
  const [indicadores, setIndicadores] = useState<TercIndicadores | null>(null);
  const navigate = useNavigate();

  const { pushErrorNotification } = usePageLoader();

  const obrasTopHeaderState = useObrasTopHeaderState();
  const pagination = useFuncionariosTercerizacaoPagination();

  // #region Funções de carregamento

  const loadIndicadoresState = useCallback(async () => {
    try {
      const response = await getAllIndicadoresUseCase.getAllIndicadores();
      setIndicadores(response);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar indicadores!");
    }
  }, [getAllIndicadoresUseCase]);

  const loadDocListaState = useCallback(
    async (funcState?: LoadPrincipalListaFuncState) => {
      try {
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage =
          funcState?.rowsPerPage ?? pagination.rowsPerPage;
        await getAllListagemDocUseCase.getAllListagemDoc(
          String(CommonConstants.moduloDocumentos.Terceirizacao),
          filteredPage,
          filteredRowsPerPage,
        );
        setDocLista(getAllListagemDocUseCase.items);
        pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
        pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
      } catch (e: unknown) {
        throw new Error("Falha ao buscar documentos!");
      }
    },
    [getAllListagemDocUseCase, pagination],
  );

  //#endregion
  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];

      jobs.push(loadDocListaState());
      jobs.push(loadIndicadoresState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadDocListaState());
      jobs.push(loadIndicadoresState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [loadDocListaState, loadIndicadoresState, pushErrorNotification]);
  //#endregion

  // #region Eventos
  const handleDownloadFile = useCallback(
    async (IdArquivoModulo: number, NomeArquivo: string) => {
      if (IdArquivoModulo > 0) {
        try {
          setLoading(true);
          await getGedArquivoDataUseCase.getGedArquivoData(
            IdArquivoModulo,
            NomeArquivo,
          );
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar documentos!"),
          );
        } finally {
          setLoading(false);
        }
      }
    },
    [getGedArquivoDataUseCase, pushErrorNotification],
  );

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        pagination.setPage(page + 1);
        await loadDocListaState({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadDocListaState, pagination, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);

        await loadDocListaState({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadDocListaState, pagination, pushErrorNotification],
  );

  //#endregion

  const handleNavegate = useCallback(
    (id: number) => {
      if (id === 1) navigate(`/FuncionarioTercerizacao_lista/0`);
      else if (id === 2) navigate(`/Contrato_lista/1/1`);
      else if (id === 3) navigate(`/FuncionarioTercerizacao_lista/null/0`);
      else if (id === 4) navigate(`/Contrato_lista/null/4`);
      else if (id === 5) navigate(`/FuncionarioTercerizacao_lista/null/null/1`);
      else if (id === 6) navigate(`/FuncionarioTercerizacao_lista/null/null/0`);
      else if (id === 7) navigate(`/MedicaoTerceirizacao_lista/1`);
      else if (id === 8) navigate(`/MedicaoTerceirizacao_lista/3`);
      else if (id === 9) navigate(`/MedicaoTerceirizacao_lista/9`);
    },
    [navigate],
  );

  const stats = useMemo(
    () => [
      {
        title: "Documentos de Funcionários Vencidos",
        value: indicadores?.documentosFuncionariosVencidos ?? 0,
        icon: ReportProblemIcon,
        colorClass: {
          bg: "bg-red-100",
          text: "text-red-600",
          bgHover: "hover:bg-red-200",
        },
        linkText: "Ir para cadastro",
        onClick: () => handleNavegate(1),
      },
      {
        title: "Documentos de Empresas Vencidos",
        value: indicadores?.documentosEmpresaVencidos ?? 0,
        icon: TaskAltIcon,
        colorClass: {
          bg: "bg-gray-100",
          text: "text-gray-600",
          bgHover: "hover:bg-gray-200",
        },
        linkText: "Ir para cadastro",
        onClick: () => handleNavegate(2),
      },
      {
        title: "Avalições de Treinamentos Atrasadas",
        value: indicadores?.avaliacoesTreinamentosAtraso ?? 0,
        icon: ShieldIcon,
        colorClass: {
          bg: "bg-orange-100",
          text: "text-orange-600",
          bgHover: "hover:bg-orange-200",
        },
        linkText: "Ir para cadastro",
        onClick: () => handleNavegate(3),
      },
      {
        title: "Contratos Vencidos",
        value: indicadores?.contratosVencidos ?? 0,
        icon: VerifiedUserIcon,
        colorClass: {
          bg: "bg-teal-100",
          text: "text-teal-600",
          bgHover: "hover:bg-teal-200",
        },
        linkText: "Ir para cadastro",
        onClick: () => handleNavegate(4),
      },
      {
        title: "Funcionários Ativos",
        value: indicadores?.funcionariosAtivos ?? 0,
        icon: EventAvailableIcon,
        colorClass: {
          bg: "bg-blue-100",
          text: "text-blue-600",
          bgHover: "hover:bg-blue-200",
        },
        linkText: "Ir para cadastro",
        onClick: () => handleNavegate(5),
      },
      {
        title: "Funcionários Inativos",
        value: indicadores?.funcionariosDemitidos ?? 0,
        icon: EventBusyIcon,
        colorClass: {
          bg: "bg-yellow-100",
          text: "text-yellow-600",
          bgHover: "hover:bg-yellow-200",
        },
        linkText: "Ir para cadastro",
        onClick: () => handleNavegate(6),
      },
      {
        title: "Medições finalizadas para aprovação",
        value: indicadores?.medicoesFinalizadas ?? 0,
        icon: NotificationImportantIcon,
        colorClass: {
          bg: "bg-teal-100",
          text: "text-teal-600",
          bgHover: "hover:bg-teal-200",
        },
        linkText: "Ir para cadastro",
        onClick: () => handleNavegate(7),
      },
      {
        title: "Medições aprovadas",
        value: indicadores?.medicoesAprovadas ?? 0,
        icon: NotificationImportantIcon,
        colorClass: {
         bg: "bg-teal-100",
          text: "text-teal-600",
          bgHover: "hover:bg-teal-200",
        },
        linkText: "Ir para cadastro",
        onClick: () => handleNavegate(8),
      },
      {
        title: "Provisão de pagamento de medições pendentes",
        value: indicadores?.medicoesPagamentoPendentes ?? 0,
        icon: NotificationImportantIcon,
        colorClass: {
          bg: "bg-orange-100",
          text: "text-orange-600",
          bgHover: "hover:bg-orange-200",
        },
        linkText: "Ir para cadastro",
        onClick: () => handleNavegate(9),
      },
    ],
    [
      handleNavegate,
      indicadores?.avaliacoesTreinamentosAtraso,
      indicadores?.contratosVencidos,
      indicadores?.documentosEmpresaVencidos,
      indicadores?.documentosFuncionariosVencidos,
      indicadores?.funcionariosAtivos,
      indicadores?.funcionariosDemitidos,
      indicadores?.medicoesAprovadas,
      indicadores?.medicoesFinalizadas,
      indicadores?.medicoesPagamentoPendentes,
    ],
  );

  return {
    loading,
    errorMessage,
    obrasTopHeaderState,
    docLista,
    pagination,
    stats,
    handleNavegate,
    handleReload,
    handlePageChange,
    handleRowsPerPageChange,
    handleDownloadFile,
  };
};
